import { inject, injectable } from 'inversify'
import { nanoid } from 'nanoid/non-secure'
import { Float32BufferAttribute, Group, type Line } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { setup } from 'xstate'
import type { AuxiliaryMouseHelper } from '@/plugins/cad/module/CADModule/auxiliaryTool/auxiliaryMouseHelper'
import {
  type MouseTipHelper,
  MouseTipId,
} from '@/plugins/cad/module/CADModule/auxiliaryTool/MouseTipHelper'
import type { CADController } from '@/plugins/cad/module/CADModule/CADController'
import { DrawEntityCommand } from '@/plugins/cad/module/CADModule/command/DrawEntityCommand'
import type { CADViewManager } from '@/plugins/cad/module/CADModule/manager/CADViewManager'
import type { HistoryManager } from '@/plugins/cad/module/CADModule/manager/HistoryManager'
import type { IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import { ArcEntity } from '@/plugins/cad/module/CADModule/model/entity'
import { createCadLine2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { MaterialUtils } from '@/plugins/cad/module/CADModule/object/material/MaterialUtils'
import type { EntitySelector } from '@/plugins/cad/module/CADModule/selector/entitySelector'
import { Setting } from '@/plugins/cad/module/common/setting'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import LineDimensionGroup from '@/plugins/cad/module/objects/LineDimensionGroup'
import { ArcUtils } from '@/plugins/cad/module/utils/ArcUtils'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { KeyCodes } from '@/shared/constants'

/**
 * 圆弧绘制模式。
 */
export enum ArcDrawMode {
  /** 三点法：起点、中间点、端点 */
  ThreePoints = 1,
  /** 两点加半径法：起点、端点、半径 */
  TwoPointsAndRadius,
}

interface IArcParams {
  center: IPoint
  radius: number
  startAngle: number
  endAngle: number
  clockwise: boolean
  segmentsNum?: number
}

const MouseTip = {
  startPoint: CadTipKey.draw.arc.startPoint,
  midPoint: CadTipKey.draw.arc.midPoint,
  endPoint: CadTipKey.draw.arc.endPoint,
  radius: CadTipKey.draw.arc.radius,
}

const arcMachine = setup({
  actions: {
    showMouseTip: (..._args: any) => {},
    entryFinished: () => {},
  },
  guards: {
    isMode: () => false,
  },
}).createMachine({
  id: 'drawArcTool',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: [
          {
            target: 'draw.twoPointsAndRadius',
            guard: { type: 'isMode', params: ArcDrawMode.TwoPointsAndRadius },
          },
          { target: 'draw.threePoints' },
        ],
      },
    },
    draw: {
      initial: 'threePoints',
      states: {
        // 模式一：三点定弧 (起点 -> 中间点 -> 端点)
        threePoints: {
          initial: 'point1',
          states: {
            point1: {
              on: { NEXT: 'point2' },
              entry: { type: 'showMouseTip', params: MouseTip.startPoint },
            },
            point2: {
              on: { NEXT: 'point3' },
              entry: { type: 'showMouseTip', params: MouseTip.midPoint },
            },
            point3: {
              on: { NEXT: '#drawArcTool.finished', PREVIOUS: 'point2' },
              entry: { type: 'showMouseTip', params: MouseTip.endPoint },
            },
          },
        },
        // 模式二：两点+半径 (起点 -> 端点 -> 半径)
        twoPointsAndRadius: {
          initial: 'point1',
          states: {
            point1: {
              on: { NEXT: 'point2' },
              entry: { type: 'showMouseTip', params: MouseTip.startPoint },
            },
            point2: {
              on: { NEXT: 'radius' },
              entry: { type: 'showMouseTip', params: MouseTip.endPoint },
            },
            radius: {
              on: { NEXT: '#drawArcTool.finished', PREVIOUS: 'point2' },
              entry: { type: 'showMouseTip', params: MouseTip.radius },
            },
          },
        },
      },
    },
    finished: { entry: 'entryFinished', type: 'final' },
  },
})

/**
 * 圆弧绘制工具，支持三点或两点加半径两种模式。
 */
@injectable()
export class DrawArcTool extends StateMachineTool<typeof arcMachine> {
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawArcTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.UnitPresentationService)
  private readonly unitPresentation!: UnitPresentationService

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = arcMachine.provide({
    actions: {
      showMouseTip: (_v, tip) => {
        this.showMouseTip(tip)
      },
      entryFinished: () => {
        this.updateEntity()
        this.exit()
      },
    },
    guards: {
      isMode: (_v, mode) => {
        return this.mode === mode
      },
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private mode: ArcDrawMode = ArcDrawMode.ThreePoints
  private points: IPoint[] = []
  private tempPoint: IPoint | null = null
  private group = new Group()
  private arcObject: Line2 | null = null
  private dimensionGroup: LineDimensionGroup | null = null
  private previewLineObject: Line | null = null
  private entity: ArcEntity | null = null
  private currentCommand: DrawEntityCommand | null = null
  private arcParams: IArcParams | null = null

  protected onInitialize(
    entityData?: Partial<ArcEntity>,
    mode: ArcDrawMode = ArcDrawMode.ThreePoints
  ): void {
    this.mode = mode
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.inject(this.auxiliaryMouseHelper)

    this.entity = this.createEntity(entityData)
    this.currentCommand = new DrawEntityCommand(
      this.entity,
      this.viewManager,
      this.handleUndo.bind(this),
      this.isCanCommit.bind(this),
      this.isCanUndoStep.bind(this)
    )
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    this.arcObject && DrawUtils.disposeObject(this.arcObject)
    this.dimensionGroup?.dispose()
    this.previewLineObject && DrawUtils.disposeObject(this.previewLineObject)
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.eject(this.auxiliaryMouseHelper)

    this.points = []
    this.tempPoint = null
    this.arcObject = null
    this.dimensionGroup = null
    this.previewLineObject = null
    this.entity = null
    this.currentCommand = null
    this.arcParams = null
  }

  /**
   * 鼠标按下：推进状态机并记录一个点。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
      if (this.tempPoint) {
        this.points.push({ x: this.tempPoint.x, y: this.tempPoint.y })
        this.tempPoint = null
        this.send({ type: 'NEXT' })
        this.updateCanvas()
        this.historyManager.refresh()
      }
    } else if (params.event.button === 2) {
      // 右键
    }
  }

  /**
   * 鼠标移动：更新待确认的圆弧预览点。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.mouseCapture(params)
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  /** 每帧刷新半径/长度标注 */
  onViewerUpdate() {
    this.dimensionGroup?.update()
  }

  // 正交/捕捉/极轴/点云 统一处理
  private mouseCapture(params: MouseEventParams) {
    if (!this.tempPoint) {
      return
    }

    // 获取起点
    const startPoint = this.points.length > 0 ? this.points[this.points.length - 1] : undefined
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })

    this.tempPoint = { x: finalPoint.x, y: finalPoint.y }
  }

  /************ 状态机状态处理 ************/
  private showMouseTip(tip: string) {
    this.mouseTipHelper.addTip(MouseTipId.Draw, tip)
  }

  /************ 渲染 ************/
  private updateCanvas() {
    this.updateObject()
  }

  private updateObject() {
    const points = [...this.points]
    if (this.tempPoint) {
      points.push(this.tempPoint)
    }
    if (points.length < 2) {
      return
    }
    const p1 = points[points.length - 2]
    const p2 = points[points.length - 1]
    // 绘制辅助预览线
    this.updatePreviewLine(p1, p2)

    // 绘制标注
    this.updateDimensionGroup(p1, p2)

    // 计算圆弧参数
    const arcParams = this.getArcParamsByMode(this.mode, points)
    this.updateArcObject(arcParams)
    this.arcParams = arcParams
  }

  private updatePreviewLine(p1: IPoint, p2: IPoint) {
    if (!this.previewLineObject) {
      const dashSize = this.controller.getPixelSizeInWorld(20)
      const materialOption = {
        color: Setting.previewLineConfig.color,
        transparent: Setting.previewLineConfig.opacity < 1,
        opacity: Setting.previewLineConfig.opacity,
        dashSize,
        gapSize: dashSize / 2,
        depthTest: false,
        depthWrite: false,
      }
      this.previewLineObject = DrawUtils.drawDashedLine(p1, p2, materialOption, this.group)
      return
    }

    const positions = [p1, p2].flatMap((p) => [p.x, p.y, 0])
    const { geometry } = this.previewLineObject
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3))
    geometry.attributes.position.needsUpdate = true
    this.previewLineObject.computeLineDistances()
  }

  private updateDimensionGroup(startPoint: IPoint, endPoint: IPoint) {
    if (this.dimensionGroup) {
      this.dimensionGroup.updateLine(startPoint, endPoint)
      return
    }
    this.dimensionGroup = new LineDimensionGroup(
      startPoint,
      endPoint,
      {},
      this.group,
      this.controller.domElement!.parentElement!,
      (point: IPoint) => this.controller.getScreenPosition(point),
      (radius: number) => this.controller.getPixelSizeInWorld(radius),
      this.unitPresentation
    )
  }

  private updateArcObject(arcParams: IArcParams | null) {
    if (!arcParams || !this.entity) {
      this.arcObject && (this.arcObject.visible = false)
      return
    }
    const segmentsNum = ArcUtils.getDynamicSegments(
      arcParams.radius,
      this.controller.getPixelSizeInWorld(1)
    )
    arcParams.segmentsNum = segmentsNum
    const options = {
      ...arcParams,
      center: { x: 0, y: 0, z: 0 },
    }
    const points = DrawUtils.getArcPoints(options)
    const geometry = DrawUtils.getLineGeometryByPoints(points)
    if (!this.arcObject) {
      const material = MaterialUtils.createMaterialByLineType(
        this.entity.lineType,
        MaterialUtils.getMaterialOptionByEntity(this.entity)
      )
      this.arcObject = createCadLine2Object(geometry, material)
      this.arcObject.position.set(arcParams.center.x, arcParams.center.y, 0)
      this.arcObject.computeLineDistances()
      this.group.add(this.arcObject)
      return
    }
    this.arcObject.geometry.dispose()
    this.arcObject.geometry = geometry
    this.arcObject.position.set(arcParams.center.x, arcParams.center.y, 0)
    this.arcObject.computeLineDistances()
    this.arcObject.visible = true
  }

  /************ 数据创建 ************/
  private createEntity(entityData?: Partial<ArcEntity>) {
    const entity = new ArcEntity({
      ...this.viewManager.getEntityDefaultData(entityData),
      id: nanoid(),
    })
    return entity
  }

  private updateEntity() {
    if (this.entity && this.arcParams) {
      Object.assign(this.entity, { ...this.arcParams })
    }
  }

  private handleUndo() {
    if (this.isCanUndoStep()) {
      this.points.pop()
      this.updateCanvas()
      this.send({ type: 'PREVIOUS' })
    }
  }

  private isCanCommit(): boolean {
    return this.entity ? this.entity.radius > 1e-2 : false
  }

  private isCanUndoStep(): boolean {
    return this.points.length > 1
  }

  private getArcParamsByMode(mode: ArcDrawMode, points: IPoint[]): IArcParams | null {
    if (mode === ArcDrawMode.ThreePoints && points.length >= 3) {
      const [p1, p2, p3] = points.slice(-3)
      return this.getArcParamsByThreePoints(p1, p2, p3)
    } else if (mode === ArcDrawMode.TwoPointsAndRadius && points.length >= 3) {
      const [p1, p2, p3] = points.slice(-3)
      return this.getArcParamsByTwoPointsAndRadius(p1, p2, p3)
    }
    return null
  }

  private getArcParamsByThreePoints(p1: IPoint, p2: IPoint, p3: IPoint): IArcParams | null {
    // 1. 使用确定性数学公式求三点圆心，避免用斜率导致的除零/Infinity问题
    const x1 = p1.x,
      y1 = p1.y
    const x2 = p2.x,
      y2 = p2.y
    const x3 = p3.x,
      y3 = p3.y

    const a1 = x1 * x1 + y1 * y1
    const a2 = x2 * x2 + y2 * y2
    const a3 = x3 * x3 + y3 * y3

    const d = 2 * (x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2))
    if (Math.abs(d) < 1e-6) {
      // 三点近似共线
      return null
    }

    const centerX = (a1 * (y2 - y3) + a2 * (y3 - y1) + a3 * (y1 - y2)) / d
    const centerY = (a1 * (x3 - x2) + a2 * (x1 - x3) + a3 * (x2 - x1)) / d
    const center = { x: centerX, y: centerY }

    // 2. 计算半径
    const radius = Math.sqrt((centerX - p1.x) ** 2 + (centerY - p1.y) ** 2)

    // 3. 计算三点相对于圆心的极角 (弧度)
    const startAngle = Math.atan2(p1.y - center.y, p1.x - center.x)
    let midAngle = Math.atan2(p2.y - center.y, p2.x - center.x)
    let endAngle = Math.atan2(p3.y - center.y, p3.x - center.x)

    // 4. 判断三点实际的几何走向 (叉积)
    const crossProduct = (p2.x - p1.x) * (p3.y - p2.y) - (p2.y - p1.y) * (p3.x - p2.x)
    const anticlockwise = crossProduct > 0

    if (anticlockwise) {
      // 逆时针：确保 start < mid < end
      while (midAngle < startAngle) midAngle += Math.PI * 2
      while (endAngle < midAngle) endAngle += Math.PI * 2
    } else {
      // 顺时针：确保 start > mid > end
      while (midAngle > startAngle) midAngle -= Math.PI * 2
      while (endAngle > midAngle) endAngle -= Math.PI * 2
    }

    return { center, radius, startAngle, endAngle, clockwise: !anticlockwise }
  }

  private getArcParamsByTwoPointsAndRadius(
    p1: IPoint,
    p2: IPoint,
    mouse: IPoint
  ): IArcParams | null {
    // 1. 基础几何数据
    const dx = p2.x - p1.x
    const dy = p2.y - p1.y
    const distP1P2 = Math.sqrt(dx * dx + dy * dy)

    if (distP1P2 < 1e-8) {
      // 两点重合，无法构造圆弧
      return null
    }

    // 2. 计算鼠标到 P1P2 直线的垂直距离作为“拱高 (h)”
    // 使用点到直线的距离公式，或者利用向量投影
    // 这里的 offset 符号决定了鼠标在直线的哪一侧
    const offset = ((mouse.x - p1.x) * dy - (mouse.y - p1.y) * dx) / distP1P2
    const h = Math.abs(offset) // 拱高

    // 3. 根据拱高计算半径 R
    // 公式：R = (h/2) + (d^2 / 8h)
    let r: number
    if (h < 1e-8) {
      // 鼠标落在线段上（或非常接近），h->0 导致除零。此时取半圆（直径为 d）的圆心为中点
      // 半径为 d/2，产生 180° 的弧（用户未明确指定拱高）
      r = distP1P2 / 2
    } else {
      r = h / 2 + (distP1P2 * distP1P2) / (8 * h)
    }

    // 4. 判定优劣弧 (Large Arc Flag)
    // 通常鼠标距离中点越远，半径越大，但在这种交互下：
    // 如果 h > distP1P2 / 2，则形成优弧 (大于半圆)
    const isLargeArc = h > distP1P2 / 2

    // 5. 判定顺逆时针
    // 根据屏幕坐标系，offset 的正负直接关联了视觉上的弯曲方向
    const anticlockwise = offset < 0

    return this.getArcByTwoPointsAndRadius(p1, p2, r, isLargeArc, anticlockwise)
  }

  private getArcByTwoPointsAndRadius(
    p1: IPoint,
    p2: IPoint,
    radius: number,
    isLargeArc: boolean = false,
    anticlockwise: boolean = true
  ): IArcParams | null {
    const d2 = (p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2
    const d = Math.sqrt(d2)

    // 1. 合法性检查：直径必须大于等于两点间距
    if (d > 2 * radius) return null

    // 2. 计算中点
    const mx = (p1.x + p2.x) / 2
    const my = (p1.y + p2.y) / 2

    // 3. 计算垂直平分线方向
    // 向量 p1p2 的方向 (dx, dy)
    const vdx = p2.x - p1.x
    const vdy = p2.y - p1.y

    // 4. 计算圆心到中点的距离 h
    const h = Math.sqrt(radius ** 2 - d2 / 4)

    // 5. 确定圆心坐标
    // 垂直向量为 (-vdy, vdx)，根据顺逆时针和优劣弧决定方向
    let factor = anticlockwise ? 1 : -1
    if (isLargeArc) factor *= -1 // 优弧方向相反

    const centerX = mx - (factor * h * vdy) / d
    const centerY = my + (factor * h * vdx) / d

    // 6. 计算角度
    const startAngle = Math.atan2(p1.y - centerY, p1.x - centerX)
    const endAngle = Math.atan2(p2.y - centerY, p2.x - centerX)

    return {
      center: { x: centerX, y: centerY },
      radius,
      startAngle,
      endAngle,
      clockwise: !anticlockwise,
    }
  }
}
